import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateSequenceInput, EnrollmentItem, Paginated, SequenceItem, SequenceStepInput, StepPreview, UpdateSequenceInput } from '@crm/shared';
import { api } from '../../api/client';

export const useSequences = () => useQuery({ queryKey: ['sequences'], queryFn: async () => (await api.get<SequenceItem[]>('/sequences')).data, refetchInterval: 30_000 });

export function useSequence(id: string | undefined) {
  return useQuery({
    queryKey: ['sequence', id],
    queryFn: async () => (await api.get<SequenceItem>(`/sequences/${id}`)).data,
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.status === 'ACTIVE' ? 30_000 : false),
  });
}

export function useEnrollments(id: string | undefined, params: { status: string; q?: string; page: number; pageSize: number }) {
  return useQuery({
    queryKey: ['enrollments', id, params],
    queryFn: async () => (await api.get<Paginated<EnrollmentItem>>(`/sequences/${id}/enrollments`, { params })).data,
    enabled: !!id,
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
}

function useRefresh() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['sequences'] });
    if (id) {
      void qc.invalidateQueries({ queryKey: ['sequence', id] });
      void qc.invalidateQueries({ queryKey: ['enrollments', id] });
    }
  };
}

/** Writes the returned sequence straight into the cache, so editors see the saved state at once. */
function useSetSequence() {
  const qc = useQueryClient();
  const refresh = useRefresh();
  return (s: SequenceItem) => {
    qc.setQueryData(['sequence', s.id], s);
    refresh(s.id);
  };
}

export function useCreateSequence() {
  const set = useSetSequence();
  return useMutation({ mutationFn: async (input: CreateSequenceInput) => (await api.post<SequenceItem>('/sequences', input)).data, onSuccess: set });
}
export function useUpdateSequence() {
  const set = useSetSequence();
  return useMutation({ mutationFn: async ({ id, ...input }: UpdateSequenceInput & { id: string }) => (await api.patch<SequenceItem>(`/sequences/${id}`, input)).data, onSuccess: set });
}
export function useSaveSteps() {
  const set = useSetSequence();
  return useMutation({ mutationFn: async ({ id, steps }: { id: string; steps: SequenceStepInput[] }) => (await api.put<SequenceItem>(`/sequences/${id}/steps`, { steps })).data, onSuccess: set });
}
export function useSequenceStatus() {
  const set = useSetSequence();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'activate' | 'pause' | 'archive' }) => (await api.post<SequenceItem & { enrolled: { added: number; skipped: number } | null }>(`/sequences/${id}/${action}`)).data,
    onSuccess: set,
  });
}
export function useDuplicateSequence() {
  const refresh = useRefresh();
  return useMutation({ mutationFn: async (id: string) => (await api.post<SequenceItem>(`/sequences/${id}/duplicate`)).data, onSuccess: () => refresh() });
}
export function useDeleteSequence() {
  const refresh = useRefresh();
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/sequences/${id}`)).data, onSuccess: () => refresh() });
}

export function useEnroll() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async ({ id, contactIds }: { id: string; contactIds?: string[] }) =>
      (contactIds ? await api.post<{ added: number; skipped: number }>(`/sequences/${id}/enrollments`, { contactIds }) : await api.post<{ added: number; skipped: number }>(`/sequences/${id}/enrollments/audience`)).data,
    onSuccess: (_r, v) => refresh(v.id),
  });
}
export function useEnrollmentAction() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async ({ id, ids, action }: { id: string; ids: string[]; action: 'pause' | 'resume' | 'remove' | 'restart' }) => (await api.post<{ updated: number }>(`/sequences/${id}/enrollments/actions`, { ids, action })).data,
    onSuccess: (_r, v) => refresh(v.id),
  });
}

export function useStepPreview(id: string | undefined, step: SequenceStepInput | null, contactId: string | null, agentId: string | null) {
  return useQuery({
    queryKey: ['step-preview', id, step, contactId, agentId],
    queryFn: async () => (await api.post<StepPreview>(`/sequences/${id}/preview`, { step, contactId, agentId })).data,
    enabled: !!id && !!step,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}
export function useStepTest() {
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; step: SequenceStepInput; to: string; contactId: string | null; agentId: string | null }) => (await api.post<{ ok: boolean; delivered: boolean; subject: string }>(`/sequences/${id}/test`, body)).data,
  });
}

export const pctOf = (part: number, whole: number) => (whole ? `${Math.round((part / whole) * 100)}%` : '—');
